// src/context/NotificationContext.js
import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { message } from 'antd';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  // Use message.useMessage() to get messageApi and contextHolder
  const [messageApi, contextHolder] = message.useMessage();

  // Memoize the notification function for performance
  const showNotification = useCallback((options = {}) => {
    const {
      type = 'info', // Default to 'info' if not specified
      content = 'No message provided', // Ensure content is not empty
      duration = 3, // Default duration of 3 seconds (or 10 as in your example)
      style = { marginTop: '20px' }, // Default style for positioning
      onClose, // Optional callback when notification closes
      key, // Optional unique key for the notification
    } = options;

    // Validate type
    const validTypes = ['success', 'error', 'warning', 'info', 'loading'];
    if (typeof type !== 'string' || !validTypes.includes(type)) {
      console.warn('Invalid notification type. Using "info" as default.');
      options.type = 'info';
    }

    // Ensure content is a string
    const displayContent = typeof content === 'string' ? content : String(content) || 'No message provided';

    try {
      messageApi.open({
        type,
        content: displayContent,
        duration,
        style,
        onClose,
        key,
      });
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }, [messageApi]); // Depend on messageApi to ensure it’s updated

  // Memoize the value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    showNotification,
  }), [showNotification]);

  return (
    <NotificationContext.Provider value={value}>
      {contextHolder} {/* Render the contextHolder from message.useMessage */}
      {children}
    </NotificationContext.Provider>
  );
};